/**
 * Derive a suggested RAG from per-check answers.
 *
 * Rule (kept deliberately simple — reviewer can always override):
 *   - any HARD check failed   → red    (contract violation)
 *   - else any SOFT check failed → amber (quality concern)
 *   - else all checks answered → green
 *   - else (some unanswered)   → null  (reviewer must finish the checks)
 *
 * "na" counts as answered and never lowers the rating.
 */

import type { CheckAnswers, PhaseCriteria, Rag, ReviewCheck } from "./types";

export interface DerivedRag {
  rag: Rag | null;
  failedHard: ReviewCheck[];
  failedSoft: ReviewCheck[];
  unanswered: ReviewCheck[];
}

export function deriveRag(criteria: PhaseCriteria, answers: CheckAnswers): DerivedRag {
  const failedHard: ReviewCheck[] = [];
  const failedSoft: ReviewCheck[] = [];
  const unanswered: ReviewCheck[] = [];

  for (const check of criteria.checks) {
    const answer = answers[check.id];
    if (!answer) {
      unanswered.push(check);
      continue;
    }
    if (answer === "fail") {
      if (check.severity === "hard") failedHard.push(check);
      else failedSoft.push(check);
    }
  }

  let rag: Rag | null;
  if (failedHard.length > 0) rag = "red";
  else if (failedSoft.length > 0) rag = "amber";
  else if (unanswered.length === 0) rag = "green";
  else rag = null;

  return { rag, failedHard, failedSoft, unanswered };
}
